import { useCallback, useEffect, useRef, useState } from "react";

export type SceneKey = "erp" | "vision" | "commerce" | "trading";

type StageHandle = {
  setScene: (key: SceneKey | null, hero?: boolean) => void;
  setSkin: (i: number) => void;
  destroy: () => void;
  ok: boolean;
};

/** Un singur motor pe pagină, ținut între navigări.
 *  Recrearea contextului WebGL la fiecare schimbare de rută ar reconstrui
 *  shaderele și atlasul de textură degeaba — de aceea instanța trăiește cât
 *  trăiește shell-ul, iar rutele doar îi spun ce scenă să monteze.
 *
 *  Importul se face la prima scenă cerută, nu la montarea shell-ului. Shell-ul
 *  e pe toate rutele, deci varianta dinainte descărca cele ~86 KB ale motorului
 *  și pe /services, /faq, /contact, pe paginile legale și pe 404 — pagini care
 *  n-au niciun model 3D și nu-l cer niciodată. */
export function useStage(skin: number) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const stageRef = useRef<StageHandle | null>(null);
  const loadRef = useRef<Promise<void> | null>(null);
  const deadRef = useRef(false);
  /* O scenă cerută înainte ca modulul să fi ajuns nu se pierde: o rețin. */
  const pendingRef = useRef<{ key: SceneKey | null; hero: boolean } | null>(null);
  const skinRef = useRef(skin);
  const [phase, setPhase] = useState(0);

  const load = useCallback(() => {
    if (loadRef.current) return loadRef.current;
    /* Pe telefon scena nu se încarcă deloc.
   
       Canvasul e `position:fixed; inset:0`, în spatele textului. Pe ecran lat
       stă lângă coloana de text și se vede ca obiect. Sub 900px coloana ocupă
       toată lățimea, iar `.blk::before` întinde peste scenă un voal de 74-90%
       fundal ca textul să rămână lizibil — adică modelul e deja spălat până la
       o fantomă pe care n-o distinge nimeni. Rămâne doar costul: 87 KB de
       motor descărcat, un context WebGL inițializat și o buclă de randare care
       consumă bateria ca să deseneze ceva ce nu se vede.
   
       Fereastra se citește o singură dată, la prima cerere de scenă: o rotire
       a telefonului nu trebuie să pornească motorul în mijlocul lecturii. */
    if (typeof window !== "undefined" && window.matchMedia("(max-width: 900px)").matches) {
      loadRef.current = Promise.resolve();
      return loadRef.current;
    }
    /* Motorul e JS portat verbatim din prototip, fără declarații de tip; de
       aceea forma întoarsă e afirmată la StageHandle mai jos. */
    loadRef.current = import("@/scene/engine.js").then(
      ({ createStage }: { createStage: (o: unknown) => StageHandle }) => {
        if (deadRef.current || !canvasRef.current) return;
        stageRef.current = createStage({
          canvas: canvasRef.current,
          onPhase: setPhase,
          skin: skinRef.current,
        }) as StageHandle;
        const p = pendingRef.current;
        if (p) stageRef.current?.setScene(p.key, p.hero);
      },
    );
    return loadRef.current;
  }, []);

  useEffect(() => {
    deadRef.current = false;
    return () => {
      deadRef.current = true;
      stageRef.current?.destroy();
      stageRef.current = null;
    };
  }, []);

  useEffect(() => {
    skinRef.current = skin;
    stageRef.current?.setSkin(skin);
  }, [skin]);

  return {
    canvasRef,
    phase,
    /* `hero` schimbă regia: pe landing obiectul se asamblează singur la
       încărcare și se stinge când hero-ul iese din ecran; pe paginile de caz
       asamblarea rămâne legată de scroll. */
    setScene: (key: SceneKey | null, hero = false) => {
      pendingRef.current = { key, hero };
      if (stageRef.current) {
        stageRef.current.setScene(key, hero);
        return;
      }
      /* `null` nu merită un import: e cererea de a nu arăta nimic. */
      if (key !== null) void load();
    },
  };
}
